import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiGet } from '../lib/api';
import { plural } from '../lib/plural';
import type { DashboardKpis, TicketsResponse } from '../types';
import { TicketRow } from '../components/TicketRow';
import { rem } from '../lib/fontSize';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const APPROX =
  "approx. : un point n'a pas de date de clôture ; on prend la date de son dernier message (à défaut, sa date de création).";
const MOIS_COURTS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const moisCourt = (ym: string): string => {
  const [y, m] = ym.split('-');
  return `${MOIS_COURTS[Number(m) - 1] ?? m} ${y?.slice(2)}`;
};

export function DashboardPage() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState<DashboardKpis | null>(null);
  const [tickets, setTickets] = useState<TicketsResponse | null>(null);

  useEffect(() => {
    void apiGet<DashboardKpis>('/api/dashboard/kpis').then(setKpis).catch(() => {});
    void apiGet<TicketsResponse>('/api/tickets?preset=a_traiter&per_page=5').then(setTickets).catch(() => {});
  }, []);

  if (!user) return null;

  return (
    <div>
      <h1 style={{ fontSize: 'var(--fs-2xl)', fontWeight: 600, margin: '0 0 4px', letterSpacing: '-0.01em' }}>
        Bonjour {user.nom_user.split(' ')[0]} 👋
      </h1>
      <p style={{ color: 'var(--text-mute)', fontSize: 'var(--fs-base)', marginTop: 0, marginBottom: 24 }}>
        {kpis
          ? `${plural(kpis.ouverts_hors_jaunes ?? kpis.ps_encours, 'point ouvert', 'points ouverts')} hors attente · ${kpis.en_attente ?? kpis.ps_valid} en attente`
          : 'Chargement…'}
      </p>

      <div className="kpi-grid">
        <Kpi
          color="#fed7aa"
          iconColor="#d97706"
          icon="⧗"
          value={kpis?.ouverts_hors_jaunes}
          label="Ouverts hors jaunes"
          title="Points non finalisés, sauf ceux en « Attente… » (dont « Attente validation temps »)"
        />
        <Kpi
          color="var(--warning-soft)"
          iconColor="#a16207"
          icon="…"
          value={kpis?.en_attente}
          label="En attente (jaunes)"
          title="Points en « Attente… », dont « Attente validation temps »"
        />
        <Kpi color="#ccfbf1" iconColor="#0d9488" icon="☺" value={kpis?.traites_annee} label="Traités cette année" title={APPROX} approx />
        <Kpi color="#ede9fe" iconColor="#7c3aed" icon="⏱" value={kpis?.traites_30j} label="Traités sur 30 jours" title={APPROX} approx />
      </div>

      {kpis?.serie_12_mois && kpis.serie_12_mois.length > 0 && <Courbe12Mois data={kpis.serie_12_mois} />}

      <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 600, margin: '0 0 12px' }}>Points prioritaires à traiter</h2>
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        {!tickets && <div style={{ padding: 20, color: 'var(--text-mute)' }}>Chargement…</div>}
        {tickets &&
          tickets.items.map((t, i) => (
            <Link key={t.display_id} to={`/tickets/${t.display_id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
              <TicketRow t={t} alt={i % 2 === 1} />
            </Link>
          ))}
        {tickets && tickets.items.length === 0 && (
          <div style={{ padding: 20, color: 'var(--text-mute)' }}>Rien à traiter pour l&apos;instant 🎉</div>
        )}
      </div>
    </div>
  );
}

/**
 * Points créés et finalisés par mois, 12 mois glissants (retour 15). Deux
 * séries sur un même axe (même unité), légende + info-bulle au survol ;
 * finalisés = approximation (date du dernier message).
 */
function Courbe12Mois({ data }: { data: { mois: string; crees: number; finalises: number }[] }) {
  const rows = data.map((d) => ({ ...d, label: moisCourt(d.mois) }));
  return (
    <section
      aria-label="Points créés et finalisés par mois, 12 derniers mois"
      style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 8px 4px', marginBottom: 24, minWidth: 0 }}
    >
      <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 600, margin: '0 8px 8px' }}>
        Créés et finalisés, 12 derniers mois{' '}
        <span title={APPROX} style={{ fontWeight: 400, fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', cursor: 'help' }}>
          (finalisés : approx.)
        </span>
      </h2>
      <div style={{ width: '100%', minWidth: 0 }}>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 'var(--fs-xs)', fill: 'var(--text-mute)' }} interval="preserveStartEnd" minTickGap={8} />
            <YAxis allowDecimals={false} width={36} tick={{ fontSize: 'var(--fs-xs)', fill: 'var(--text-mute)' }} />
            <Tooltip
              contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: 'var(--fs-xs)' }}
              labelStyle={{ color: 'var(--text)' }}
            />
            <Legend wrapperStyle={{ fontSize: 'var(--fs-xs)', color: 'var(--text)' }} />
            <Line type="monotone" dataKey="crees" name="Créés" stroke="var(--chart-1)" strokeWidth={2} dot={{ r: 4 }} isAnimationActive={false} />
            <Line
              type="monotone"
              dataKey="finalises"
              name="Finalisés (approx.)"
              stroke="var(--chart-2)"
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={{ r: 4 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function Kpi({
  icon,
  value,
  label,
  color,
  iconColor,
  link,
  title,
  approx,
}: {
  icon: string;
  value: number | undefined;
  label: string;
  color: string;
  iconColor: string;
  link?: string;
  /** Info-bulle (ex. « approx. … »). */
  title?: string;
  /** Chiffre approché : « ≈ » devant la valeur. */
  approx?: boolean;
}) {
  // Contenu centré (icône, chiffre, libellé) ; tailles en rem, elles suivent
  // la taille de texte choisie. Le libellé peut passer à la ligne en 390 px.
  const card = (
    <div
      title={title}
      style={{
        background: 'var(--bg)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: '14px 12px',
        cursor: link ? 'pointer' : 'default',
        transition: 'border-color 0.15s',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        height: '100%',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: rem(44),
          height: rem(44),
          borderRadius: 10,
          background: color,
          color: iconColor,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'var(--fs-xl)',
          lineHeight: 1,
          marginBottom: 8,
        }}
      >
        {icon}
      </div>
      <div style={{ fontSize: 'var(--fs-3xl)', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.15 }}>
        {value !== undefined ? (approx ? `≈ ${value}` : value) : '–'}
      </div>
      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', overflowWrap: 'anywhere' }}>{label}</div>
    </div>
  );
  return link ? (
    <Link to={link} style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}>
      {card}
    </Link>
  ) : (
    card
  );
}
